import { useMutation, useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import axios from 'redaxios'
import { AppHTTPService } from '../../../api/http-service'
import { BillingSubscriptionItem, Product } from '../../../api/types'

export interface StripeLineItem {
  price: string
  quantity?: number
}

export const useBillingPage = () => {
  const [lineItems, setLineItems] = useState<StripeLineItem[]>([])
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false)
  const [isCheckoutError, setIsCheckoutError] = useState(false)

  const { data: dataSubscriptions, refetch: refetchSubscription } = useQuery({
    queryKey: ['getSubscription'],
    queryFn: () => AppHTTPService.axios.get(`/v1/billing/subscriptions`),
  })

  const subscription = useMemo(() => dataSubscriptions?.data?.data?.[0], [dataSubscriptions?.data])

  const {
    data: allProducts,
    isError: isGetProductsError,
    isLoading: isGetProductsLoading,
  } = useQuery({
    queryKey: ['getProducts'],
    queryFn: () =>
      axios.get(`${window.flatfile.configService.get('VITE_BASE_API_URL')}/v1/billing/products`, {
        params: { 'filterMetadata[displayInUI]': true },
      }),
  })

  const productsData: Product[] = useMemo(() => {
    if (!allProducts?.data.data) {
      return []
    }
    return allProducts.data.data
  }, [allProducts?.data.data])

  const { mutateAsync: checkout } = useMutation({
    mutationKey: ['createCheckoutSession'],
    mutationFn: () =>
      axios.post(`${window.flatfile.configService.get('VITE_BASE_API_URL')}/v1/billing/checkoutsession/create`, {
        lineItems,
        successUrl: `${window.flatfile.configService.get('VITE_CHECKOUT_RETURN_URL')}/billing?checkoutSuccess=true`,
      }),
  })

  const handleCheckoutClick = async () => {
    setIsCheckoutError(false)
    setIsCheckoutLoading(true)
    try {
      const response = await checkout()
      if (response?.data?.data?.url) {
        window.location.href = response.data.data.url
      } else {
        throw new Error()
      }
    } catch (error) {
      setIsCheckoutLoading(false)
      setIsCheckoutError(true)
    }
  }

  const {
    mutate: updateSubscription,
    isError: subscriptionUpdateIsError,
    isPending: subscriptionUpdateIsLoading,
  } = useMutation({
    mutationKey: ['updateSubscription'],
    mutationFn: () =>
      axios
        .put(`${window.flatfile.configService.get('VITE_BASE_API_URL')}/v1/billing/subscription/${subscription?.id}`, {
          lineItems: lineItems as any,
        })
        .then(() => {
          window.location.replace(
            `${window.flatfile.configService.get('VITE_CHECKOUT_RETURN_URL')}/billing?updateSubscriptionSuccess=true`
          )
        }),
  })

  const {
    mutate: cancelSubscription,
    isError: subscriptionCancellationIsError,
    isPending: subscriptionCancellationIsLoading,
  } = useMutation({
    mutationKey: ['cancelSubscription'],
    mutationFn: async () => {
      await axios.delete(
        `${window.flatfile.configService.get('VITE_BASE_API_URL')}/v1/billing/subscription/${subscription?.id}`
      )
      await refetchSubscription()
      window.location.replace(
        `${window.flatfile.configService.get('VITE_BASE_DASHBOARD_URL')}/billing?cancelSuccess=true`
      )
    },
  })

  const subscriptionItems: BillingSubscriptionItem[] = useMemo(() => {
    return subscription?.items || []
  }, [subscription?.items])

  return {
    productsData,
    handleCheckoutClick,
    lineItems,
    setLineItems,
    isGetProductsError,
    isGetProductsLoading,
    isCheckoutLoading,
    isCheckoutError,
    subscription,
    subscriptionItems,
    updateSubscription,
    subscriptionUpdateIsError,
    subscriptionUpdateIsLoading,
    cancelSubscription,
    subscriptionCancellationIsError,
    subscriptionCancellationIsLoading,
  }
}
